/**
 * Utility Helper Functions
 */

import { format, formatDistance, intervalToDuration } from 'date-fns';

/**
 * Format a timestamp to a readable date string
 * @param {string|Date} date - Date to format
 * @param {string} formatString - Format string (default: 'MMM d, yyyy h:mm a')
 * @returns {string} Formatted date string
 */
export const formatDate = (date, formatString = 'MMM d, yyyy h:mm a') => {
  if (!date) return 'N/A';
  try {
    return format(new Date(date), formatString);
  } catch (error) {
    return 'Invalid date';
  }
};

/**
 * Format a timestamp relative to now (e.g., "2 hours ago")
 * @param {string|Date} date - Date to format
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (date) => {
  if (!date) return 'Never';
  try {
    return formatDistance(new Date(date), new Date(), { addSuffix: true });
  } catch (error) {
    return 'Invalid date';
  }
};

/**
 * Format duration in seconds to human-readable string
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration (e.g., "2h 30m")
 */
export const formatDurationSeconds = (seconds) => {
  if (!seconds || seconds < 0) return '0m';

  const duration = intervalToDuration({ start: 0, end: seconds * 1000 });
  const parts = [];

  if (duration.days) parts.push(`${duration.days}d`);
  if (duration.hours) parts.push(`${duration.hours}h`);
  if (duration.minutes) parts.push(`${duration.minutes}m`);

  return parts.length > 0 ? parts.join(' ') : '0m';
};

/**
 * Get status badge variant based on status string
 * @param {string} status - Status string
 * @returns {string} Bootstrap variant
 */
export const getStatusVariant = (status) => {
  switch (status?.toUpperCase()) {
    case 'SUCCESS':
      return 'success';
    case 'FAILED':
    case 'ERROR':
      return 'danger';
    default:
      return 'secondary';
  }
};

/**
 * Parse API response safely
 * @param {string} apiResponse - JSON string from API
 * @returns {Object|null} Parsed object or null
 */
export const parseApiResponse = (apiResponse) => {
  if (!apiResponse) return null;
  try {
    return JSON.parse(apiResponse);
  } catch (error) {
    return null;
  }
};

/**
 * Parse API request (CURL commands array)
 * @param {string} apiRequest - JSON string containing array of requests
 * @returns {Array|null} Parsed array or null
 */
export const parseApiRequest = (apiRequest) => {
  if (!apiRequest) return null;
  try {
    const parsed = JSON.parse(apiRequest);
    return Array.isArray(parsed) ? parsed : null;
  } catch (error) {
    return null;
  }
};

/**
 * Truncate text to a maximum length
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text
 */
export const truncate = (text, maxLength = 50) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Format a parking window (HH:MM - HH:MM) with 12-hour format and day indicator
 * @param {string} startTime - Start time in HH:MM format
 * @param {string} endTime - End time in HH:MM format
 * @returns {string} Formatted range (e.g., "7:00 PM to 8:00 AM (next day)")
 */
export const formatTimeRange12Hour = (startTime, endTime) => {
  if (!startTime || !endTime) return '';
  const convert24To12 = (time24) => {
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const [startHour] = startTime.split(':').map(Number);
  const [endHour] = endTime.split(':').map(Number);

  const isNextDay = endHour < startHour || (endHour === startHour && endTime < startTime);

  return `${convert24To12(startTime)} to ${convert24To12(endTime)} (${isNextDay ? 'next day' : 'same day'})`;
};

/**
 * Format a single time (HH:MM) to 12-hour format
 * @param {string} time - Time in HH:MM format
 * @returns {string} Formatted time (e.g., "7:00 PM")
 */
export const formatTime12Hour = (time) => {
  if (!time) return '';
  const [hours, minutes] = time.split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return '';
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

/**
 * Validate time format (HH:MM)
 * @param {string} time - Time string to validate
 * @returns {boolean} True if valid
 */
export const isValidTimeFormat = (time) => {
  const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
  return timeRegex.test(time);
};

/**
 * Extract permit hash from API response
 * @param {string} apiResponse - JSON string from API (now an array of responses)
 * @returns {string|null} Permit hash or null
 */
export const extractPermitHash = (apiResponse) => {
  const parsed = parseApiResponse(apiResponse);

  // Handle new array format: [{ idx: 1, data: {...} }, ...]
  if (Array.isArray(parsed)) {
    // Find the first response with permit data (should be idx: 1)
    const permitResponse = parsed.find(r => r.idx === 1);
    return permitResponse?.data?.permits?.item || null;
  }

  // Fallback to old format for backward compatibility
  return parsed?.data?.permits?.item || null;
};

/**
 * Generate permit link from hash
 * @param {string} permitHash - Permit hash/container ID
 * @returns {string|null} Full permit URL or null
 */
export const getPermitLink = (permitHash) => {
  if (!permitHash) return null;
  return `https://metro.parkingattendant.com/p/${permitHash}`;
};

/**
 * Format month string to readable format
 * @param {string} monthStr - Month in YYYY-MM format
 * @returns {string} Formatted month (e.g., "January 2026")
 */
export const formatMonthLabel = (monthStr) => {
  if (!monthStr) return 'N/A';
  try {
    // Parse YYYY-MM and ensure we use the correct date
    // Add 'T12:00:00' to avoid timezone offset issues
    const date = new Date(`${monthStr}-01T12:00:00`);
    return format(date, 'MMMM yyyy');
  } catch (error) {
    return monthStr;
  }
};

/**
 * Get current month in YYYY-MM format
 * @returns {string} Current month
 */
export const getCurrentMonth = () => {
  return format(new Date(), 'yyyy-MM');
};

/**
 * Get bar color based on hours used
 * @param {number} hours - Hours used
 * @returns {string} Color hex code
 */
export const getParkingHoursColor = (hours) => {
  if (hours > 240) return '#dc3545'; // Red
  if (hours >= 200) return '#ffc107'; // Yellow
  return '#28a745'; // Green
};
